import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import type { MeasuredAnnotation } from './HighlightOverlay';
import type {
  Annotation,
  AnnotationReference,
  AnnotationTextColor,
} from './annotationTypes';
import { InlineNoteCard } from './InlineNoteCard';
import styles from './NoteGutter.module.css';

const DEFAULT_CARD_HEIGHT = 128;
const NOTE_CARD_GAP = 16;

interface PositionedNote {
  annotation: Annotation;
  cardTop: number;
  connectorOffset: number;
}

interface InlineNoteLayout {
  connectorWidth: number;
  left: number;
  width: number;
}

interface NoteGutterProps {
  activeAnnotation: AnnotationReference | null;
  highlightColor: string;
  layout: InlineNoteLayout | null;
  noteTextColor?: AnnotationTextColor;
  notes: MeasuredAnnotation[];
  onDeleteAnnotation: (annotation: Annotation) => Promise<void> | void;
  onSetActiveAnnotation: (reference: AnnotationReference | null) => void;
  onUpdateAnnotation: (annotation: Annotation) => Promise<void> | void;
}

function createReference(annotation: Annotation): AnnotationReference {
  return {
    annotationId: annotation.id,
    contentUnitId: annotation.contentUnitId,
  };
}

function getPositionedNotes(
  notes: MeasuredAnnotation[],
  heights: Record<string, number>,
): PositionedNote[] {
  const sortedNotes = [...notes].sort((left, right) => {
    const leftTop = left.rects[0]?.top ?? 0;
    const rightTop = right.rects[0]?.top ?? 0;
    return leftTop - rightTop;
  });
  const positionedNotes: PositionedNote[] = [];
  let previousBottom: number | null = null;

  sortedNotes.forEach(({ annotation, rects }) => {
    const firstRect = rects[0];

    if (!firstRect) {
      return;
    }

    const naturalTop = firstRect.top;
    const cardTop =
      previousBottom === null
        ? naturalTop
        : Math.max(naturalTop, previousBottom + NOTE_CARD_GAP);
    const cardHeight = heights[annotation.id] ?? DEFAULT_CARD_HEIGHT;

    previousBottom = cardTop + cardHeight;
    positionedNotes.push({
      annotation,
      cardTop,
      connectorOffset: Math.max(18, firstRect.top + firstRect.height / 2 - cardTop),
    });
  });

  return positionedNotes;
}

export function NoteGutter({
  activeAnnotation,
  highlightColor,
  layout,
  noteTextColor,
  notes,
  onDeleteAnnotation,
  onSetActiveAnnotation,
  onUpdateAnnotation,
}: NoteGutterProps) {
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
  const positionedNotes = useMemo(
    () => getPositionedNotes(notes, cardHeights),
    [cardHeights, notes],
  );

  useEffect(() => {
    if (!activeAnnotation || !layout) {
      return;
    }

    buttonRefs.current[activeAnnotation.annotationId]?.focus();
  }, [activeAnnotation, layout, positionedNotes]);

  if (!layout) {
    return null;
  }

  return (
    <div
      className={styles.gutter}
      style={
        {
          left: layout.left,
          width: layout.width,
        } as CSSProperties
      }
    >
      {positionedNotes.map(({ annotation, cardTop, connectorOffset }) => {
        const isActive =
          activeAnnotation?.annotationId === annotation.id &&
          activeAnnotation.contentUnitId === annotation.contentUnitId;

        return (
          <div
            className={styles.noteSlot}
            key={annotation.id}
            ref={(node) => {
              if (!node) {
                return;
              }

              const nextHeight = Math.ceil(node.getBoundingClientRect().height);

              setCardHeights((current) =>
                current[annotation.id] === nextHeight
                  ? current
                  : {
                      ...current,
                      [annotation.id]: nextHeight,
                    },
              );
            }}
            style={
              {
                '--connector-top': `${connectorOffset}px`,
                '--connector-width': `${layout.connectorWidth}px`,
              } as CSSProperties
            }
          >
            <InlineNoteCard
              annotation={annotation}
              highlightColor={highlightColor}
              isActive={isActive}
              isEditing={editingAnnotationId === annotation.id}
              noteTextColor={noteTextColor}
              onCancelEdit={() => setEditingAnnotationId(null)}
              onDelete={async () => {
                await onDeleteAnnotation(annotation);
                setEditingAnnotationId((current) =>
                  current === annotation.id ? null : current,
                );
              }}
              onEdit={() => {
                onSetActiveAnnotation(createReference(annotation));
                setEditingAnnotationId(annotation.id);
              }}
              onSave={async (note) => {
                await onUpdateAnnotation({
                  ...annotation,
                  note,
                });
                setEditingAnnotationId(null);
              }}
              ref={(node) => {
                buttonRefs.current[annotation.id] = node;
              }}
              top={cardTop}
            />
          </div>
        );
      })}
    </div>
  );
}
